import React, { useEffect, useLayoutEffect, useState, useRef, useContext } from "react";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { supabase } from './data/supabaseClient';
import {
  FaExclamationTriangle,
  FaCar,
  FaMapMarkerAlt,
  FaUser,
  FaBox,
  FaLocationArrow,
  FaExpandAlt,
  FaCompressAlt,
  FaFilter,
  FaSlidersH
} from "react-icons/fa";
import { AuthContext } from './Provider';
import { useToast } from './hooks/ToastContext';
import { decodePolyline } from '../utils/polylineUtils';
import { BRAND_MAP_STYLE, BRAND_MAP_STYLE_DARK } from '../utils/mapStyle';
import Spinner from './common/Spinner';
import MapLoadingSkeleton from './common/MapLoadingSkeleton';
import "./RouteMapView.css";

/**
 * Component that displays post route and matches on a map
 * 
 * @param {Object} props
 * @param {Object} props.post - The post data
 * @param {Array} props.matches - Array of matching posts/packages
 * @param {Function} props.onViewMatch - Function to view match details
 * @param {Function} props.onMessageUser - Function to message a user
 * @param {boolean} props.showRadiusSelector - Whether to show radius selector
 * @param {number} props.initialRadius - Initial radius in km
 */
const PostRouteMapView = ({
  post,
  matches = [],
  onViewMatch,
  onMessageUser,
  onFilterChange,
  onExpandChange,
  showRadiusSelector = false,
  initialRadius = 10
}) => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const polylinesRef = useRef([]);
  const mainPolylineRef = useRef(null);
  const infoWindowRef = useRef(null);
  const boundsRef = useRef(null);
  const markerClustererRef = useRef(null);
  const matchMarkersRef = useRef([]); // Current match markers (for reliable recenter/refit)
  const selectedMarkerRef = useRef(null); // Currently highlighted match marker
  const mapBodyRef = useRef(null); // Positioning context for the cluster popover
  const clusterPopoverRef = useRef(null); // The popover element (for edge-clamping)
  
  // State
  const [map, setMap] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [showFullMapView, setShowFullMapView] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedMatchRoute, setSelectedMatchRoute] = useState(null);
  // Cluster popover: { matches: [...], pos: {x,y}|null } — list of posts stacked
  // at one location so each is individually selectable (instead of the old
  // zoom-and-hope on identical coordinates).
  const [clusterPopover, setClusterPopover] = useState(null);
  const [popoverPos, setPopoverPos] = useState(null); // clamped {left, top}, measured after render
  const clusterClickRef = useRef(null); // latest cluster-click handler (clusterer is created once)
  const [showFilters, setShowFilters] = useState(false); // filter dropdown open (React-driven)
  const [filters, setFilters] = useState({
    showDrivers: true,
    showPassengers: true,
    showPackages: true
  });
  const [searchRadius, setSearchRadius] = useState(initialRadius);
  const [maxAllowedRadius, setMaxAllowedRadius] = useState(5); // Default to Free plan radius
  const [mapMarkers, setMapMarkers] = useState({
    startPoint: null,
    endPoint: null,
    matches: []
  });

  // Check user's subscription for max allowed route matching radius
  useEffect(() => {
    if (!user) return;
    
    const checkSubscriptionLimits = async () => {
      try {
        const { data: limitData, error: limitError } = await supabase.rpc('check_subscription_limit', {
          user_id: user.id,
          feature_name: 'route_match_radius_km'
        });
        
        if (limitError) {
          console.error('Error checking subscription limits:', limitError);
          return;
        }
        
        if (limitData?.value) {
          const radius = parseInt(limitData.value, 10);
          setMaxAllowedRadius(radius);
          
          // If current radius exceeds allowed limit, reset to max allowed
          if (searchRadius > radius) {
            setSearchRadius(radius);
            addToast({
              type: 'info',
              title: t('subscription.limit_applied') || 'Subscription Limit Applied',
              description: `Your current plan allows maximum ${radius}km matching radius.`
            });
          }
        }
      } catch (error) {
        console.error('Error checking subscription limits:', error);
      }
    };
    
    checkSubscriptionLimits();
  }, [user, addToast, t, searchRadius]);

  // Check if Google Maps API is loaded
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps) {
        setIsMapLoading(false);
      } else {
        console.warn("Google Maps API not loaded yet, waiting...");
        setTimeout(checkGoogleMapsLoaded, 500);
      }
    };

    checkGoogleMapsLoaded();
  }, []);

  // Clean up markers and polylines when component unmounts
  useEffect(() => {
    return () => {
      // Close any open info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      
      // Clear marker clusterer
      if (markerClustererRef.current) {
        markerClustererRef.current.clearMarkers();
        markerClustererRef.current = null;
      }
      
      // Clear all markers
      clearAllMarkers();
    };
  }, []);
  
  // Helper to clear all markers and polylines
  const clearAllMarkers = () => {
    // Clear marker clusterer first
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
    }
    
    // Clear start/end markers (if they exist)
    if (mapMarkers.startPoint) {
      mapMarkers.startPoint.setMap(null);
    }
    
    if (mapMarkers.endPoint) {
      mapMarkers.endPoint.setMap(null);
    }
    
    // Clear match markers
    mapMarkers.matches.forEach(marker => {
      if (marker) marker.setMap(null);
    });
    
    // Clear all polylines
    polylinesRef.current.forEach(polyline => {
      if (polyline) polyline.setMap(null);
    });
    
    // Clear main route polyline
    if (mainPolylineRef.current) {
      mainPolylineRef.current.setMap(null);
      mainPolylineRef.current = null;
    }
    
    // Reset the markers state
    setMapMarkers({
      startPoint: null,
      endPoint: null,
      matches: []
    });
    
    // Clear the polylines ref
    polylinesRef.current = [];
  };

  // --- Centering helpers ----------------------------------------------------
  // Rebuild bounds fresh from the current route + match markers each time. The
  // old code accumulated points on a single shared LatLngBounds, which left
  // stale points in and routes half-off-screen.
  const buildBounds = () => {
    const bounds = new window.google.maps.LatLngBounds();
    if (mainPolylineRef.current) {
      mainPolylineRef.current.getPath().forEach(p => bounds.extend(p));
    }
    matchMarkersRef.current.forEach(m => {
      const pos = m && m.getPosition ? m.getPosition() : null;
      if (pos) bounds.extend(pos);
    });
    return bounds;
  };

  const fitToContent = (padding = 48) => {
    if (!map) return;
    const bounds = buildBounds();
    if (bounds.isEmpty()) return;
    boundsRef.current = bounds;
    map.fitBounds(bounds, padding);
  };

  // --- Overlapping-marker spread -------------------------------------------
  // Many posts share the exact same coordinate (e.g. every "Rīga" post), so
  // they stack into one cluster that never separates no matter how far you zoom.
  // Fan same-coordinate markers onto a small ring so each is individually
  // clickable once the cluster is expanded. (Route drawing uses the DB polyline,
  // not the marker position, so the tiny offset is display-only.)
  const coordKey = (m) =>
    `${parseFloat(m.start_point_lat).toFixed(4)},${parseFloat(m.start_point_lng).toFixed(4)}`;

  // --- Branded match markers -----------------------------------------------
  // Custom SVG pins (matching the branded cluster icons) instead of Google's
  // stock colored dots, with a distinct larger "selected" variant.
  // Package = terracotta, matching --color-role-package (#C97A4A) in design-system.css.
  const ROLE_COLORS = { Driver: '#4285F4', Passenger: '#9C27B0', Package: '#C97A4A' };
  const roleColor = (role) => ROLE_COLORS[role] || '#FF7043';

  const markerIcon = (role, selected = false) => {
    const color = roleColor(role);
    const w = selected ? 40 : 30;
    const h = Math.round(w * 1.28);
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 40 51">` +
        `<path d="M20 1.5C10.6 1.5 3 9.1 3 18.5c0 12 17 30 17 30s17-18 17-30C37 9.1 29.4 1.5 20 1.5Z" fill="${color}" stroke="#ffffff" stroke-width="${selected ? 3 : 2.5}"/>` +
        `<circle cx="20" cy="18.5" r="${selected ? 8 : 6.5}" fill="#ffffff"/>` +
        (selected ? `<circle cx="20" cy="18.5" r="4" fill="${color}"/>` : '') +
      `</svg>`;
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(w, h),
      anchor: new window.google.maps.Point(w / 2, h)
    };
  };

  // Highlight the marker belonging to a match (revert any previous highlight).
  const highlightMatchMarker = (match) => {
    const prev = selectedMarkerRef.current;
    if (prev) {
      prev.setIcon(markerIcon(prev.matchData?.match_role, false));
      prev.setZIndex(500);
      selectedMarkerRef.current = null;
    }
    if (!match) return;
    const marker = matchMarkersRef.current.find(
      m => m.matchData?.match_id === match.match_id && m.matchData?.match_type === match.match_type
    );
    if (marker) {
      marker.setIcon(markerIcon(marker.matchData?.match_role, true));
      marker.setZIndex(1000);
      selectedMarkerRef.current = marker;
    }
  };

  const spreadPosition = (lat, lng, index, total) => {
    if (total <= 1) return { lat, lng };
    const radiusM = 35 + total * 6; // wider ring for bigger stacks
    const angle = (2 * Math.PI * index) / total;
    const dLat = (radiusM * Math.cos(angle)) / 111320;
    const dLng = (radiusM * Math.sin(angle)) / (111320 * Math.cos((lat * Math.PI) / 180));
    return { lat: lat + dLat, lng: lng + dLng };
  };

  // Initialize map
  useEffect(() => {
    if (isMapLoading || mapInitialized || !mapRef.current || !post) return;

    try {
      console.log("Initializing Google Maps for post route map view...");

      // Default to Latvia if no coordinates
      let defaultLocation = { lat: 56.9677, lng: 24.1056 };
      
      // Create map instance first. Drop the stock Map/Satellite toggle (it clashes
      // with the branded muted palette) and pick the theme-matched style.
      const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'option2';
      const mapOptions = {
        center: defaultLocation,
        zoom: 9,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        styles: isDarkTheme ? BRAND_MAP_STYLE_DARK : BRAND_MAP_STYLE
      };
      
      const newMap = new window.google.maps.Map(mapRef.current, mapOptions);
      setMap(newMap);
      
      // Initialize bounds for auto-zooming
      boundsRef.current = new window.google.maps.LatLngBounds();
      
      // Initialize marker clusterer with custom styling and click handling.
      // Clicking a cluster opens a list popover of the posts stacked there
      // rather than zooming (which never separates identical-coordinate posts).
      const clustererOptions = {
        gridSize: 60,
        maxZoom: 15,
        minimumClusterSize: 2,
        onClusterClick: (event, cluster, clusterMap) => {
          if (clusterClickRef.current) clusterClickRef.current(event, cluster, clusterMap);
        },
        styles: [
          {
            url: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" fill="${getComputedStyle(document.documentElement).getPropertyValue('--accent-color2').trim() || '#5a9dc9'}" stroke="white" stroke-width="2" opacity="0.8"/>
                <text x="20" y="26" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="12" font-weight="bold">LABEL_TEXT</text>
              </svg>
            `),
            width: 40,
            height: 40,
            textColor: 'white',
            textSize: 12
          },
          {
            url: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="50" height="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                <circle cx="25" cy="25" r="23" fill="${getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim() || '#9FC131'}" stroke="white" stroke-width="2" opacity="0.8"/>
                <text x="25" y="31" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="14" font-weight="bold">LABEL_TEXT</text>
              </svg>
            `),
            width: 50,
            height: 50,
            textColor: 'white',
            textSize: 14
          },
          {
            url: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="30" r="28" fill="${getComputedStyle(document.documentElement).getPropertyValue('--main-color').trim() || '#313a3a'}" stroke="white" stroke-width="3" opacity="0.9"/>
                <text x="30" y="37" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="16" font-weight="bold">LABEL_TEXT</text>
              </svg>
            `),
            width: 60,
            height: 60,
            textColor: 'white',
            textSize: 16
          }
        ]
      };
      
      markerClustererRef.current = new MarkerClusterer({
        map: newMap,
        ...clustererOptions
      });
      
      console.log('MarkerClusterer initialized successfully');
      
      setMapInitialized(true);
      console.log("Map and marker clusterer initialized successfully for post route map view");
    } catch (e) {
      console.error("Error initializing map:", e);
      setError(t('errors.map_initialization_failed'));
    }
  }, [isMapLoading, post, t]);
  
  // Add a click listener to the map to remove the route when clicking elsewhere
  useEffect(() => {
    if (!map) return;
    
    // Add click listener to map
    const clickListener = map.addListener('click', () => {
      console.log('Map clicked, clearing all routes');
      // Dismiss the cluster popover / filter dropdown on map interaction
      setClusterPopover(null);
      setShowFilters(false);
      // Remove any existing selected match route using our clearSelectedRoute function
      clearSelectedRoute();
      
      // Close any open info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
      
      // Additionally, clear all polylines as a fail-safe
      polylinesRef.current.forEach(polyline => {
        if (polyline) polyline.setMap(null);
      });
      polylinesRef.current = [];
    });
    
    // Clean up listener when component unmounts
    return () => {
      if (clickListener) {
        window.google.maps.event.removeListener(clickListener);
      }
    };
  }, [map]);

  // Draw post route when map is initialized
  useEffect(() => {
    if (!mapInitialized || !map || !post) return;
    
    // Clear previous markers/routes
    clearAllMarkers();
    
    console.log("Drawing post route on map");
    
    // Get route points from the polyline
    const routePolyline = post.route_polyline;
    if (!routePolyline) {
      console.error("No route polyline found for post");
      setError(t('errors.no_route_data'));
      return;
    }
    
    const path = decodePolyline(routePolyline);
    if (path.length < 2) {
      console.error("Not enough points in polyline");
      setError(t('errors.invalid_route_data'));
      return;
    }
    
    console.log(`Decoded ${path.length} points from polyline`);
    
    // Draw the route polyline. Use the brand accent (green) for the user's OWN
    // route so it reads distinctly from the match routes (blue/purple/yellow),
    // which previously collided with the near-identical #0066CC / #4285F4 blues.
    const ownRouteColor =
      getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim() || '#9FC131';
    const polyline = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: ownRouteColor,
      strokeOpacity: 0.9,
      strokeWeight: 6,
      zIndex: 300
    });
    
    polyline.setMap(map);
    mainPolylineRef.current = polyline;
    
    // Update the markers state (no start/end markers)
    setMapMarkers(prev => ({
      ...prev,
      startPoint: null,
      endPoint: null
    }));
    
    // Fit to the route now, then re-fit once the container has settled. The map
    // frequently mounts while the modal is still animating/sizing, which left the
    // route half-off-screen until the user manually panned.
    fitToContent();
    window.google.maps.event.addListenerOnce(map, 'idle', () => fitToContent());
    setTimeout(() => {
      if (!map) return;
      window.google.maps.event.trigger(map, 'resize');
      fitToContent();
    }, 400);
  }, [mapInitialized, map, post, t]);
  
  // Add match markers to map when matches change
  useEffect(() => {
    if (!mapInitialized || !map || !markerClustererRef.current) return;

    // Always clear previous match markers from clusterer
    markerClustererRef.current.clearMarkers();

    // Close any open cluster popover (its markers are about to be recreated)
    setClusterPopover(null);
    selectedMarkerRef.current = null; // markers are being recreated below

    // Clear any selected match routes when matches change
    if (selectedMatchRoute) {
      selectedMatchRoute.setMap(null);
      setSelectedMatchRoute(null);
    }

    // Clear all polylines
    polylinesRef.current.forEach(polyline => {
      if (polyline) polyline.setMap(null);
    });
    polylinesRef.current = [];

    // If no matches, just clear and return
    if (!matches || matches.length === 0) {
      console.log('No matches to display, markers cleared');
      matchMarkersRef.current = [];
      setMapMarkers(prev => ({
        ...prev,
        matches: []
      }));
      return;
    }

    // Set up new match markers
    const newMatchMarkers = [];
    console.log(`Adding ${matches.length} match markers to clustered map`);

    // Filter matches based on filters
    const filteredMatches = matches.filter(match => {
      if (match.match_role === 'Driver' && !filters.showDrivers) return false;
      if (match.match_role === 'Passenger' && !filters.showPassengers) return false;
      if (match.match_role === 'Package' && !filters.showPackages) return false;
      return true;
    });

    // Count how many matches share each coordinate so identical-location stacks
    // can be fanned out (see spreadPosition).
    const coordCounts = {};
    filteredMatches.forEach(m => {
      if (!m.start_point_lat || !m.start_point_lng) return;
      const k = coordKey(m);
      coordCounts[k] = (coordCounts[k] || 0) + 1;
    });
    const coordSeen = {};

    // Process each match
    filteredMatches.forEach(match => {
      try {
        // Create point from match coordinates
        if (!match.start_point_lat || !match.start_point_lng) {
          console.warn("Match missing start coordinates:", match);
          return;
        }

        // Base position, fanned out if several matches share this exact coordinate.
        const baseLat = parseFloat(match.start_point_lat);
        const baseLng = parseFloat(match.start_point_lng);
        const key = coordKey(match);
        const total = coordCounts[key] || 1;
        const idx = coordSeen[key] || 0;
        coordSeen[key] = idx + 1;
        const position = spreadPosition(baseLat, baseLng, idx, total);

        // Create marker (note: don't set map here, clusterer will handle it)
        const matchMarker = new window.google.maps.Marker({
          position,
          title: `${match.username} - ${match.match_role}`,
          icon: markerIcon(match.match_role, false),
          zIndex: 500,
          // Store match data with marker
          matchData: match
        });
        
        // Click a marker → select that match (draw its route + notify parent)
        matchMarker.addListener('click', () => selectMatch(match));
        
        // Add marker to list
        newMatchMarkers.push(matchMarker);
        
        // Add point to bounds
        boundsRef.current.extend(position);
      } catch (e) {
        console.error("Error creating match marker:", e);
      }
    });
    
    // Add all markers to the clusterer at once
    if (newMatchMarkers.length > 0) {
      markerClustererRef.current.addMarkers(newMatchMarkers);
      console.log(`Added ${newMatchMarkers.length} markers to clusterer`);
    }
    
    // Track markers for recenter/refit and update state
    matchMarkersRef.current = newMatchMarkers;
    setMapMarkers(prev => ({
      ...prev,
      matches: newMatchMarkers
    }));

    // Refit to include the route + all match markers
    if (newMatchMarkers.length > 0) {
      fitToContent();
    }
  }, [mapInitialized, map, matches, onViewMatch, onMessageUser, t, filters]);
  
  // Function to clear the selected match route
  const clearSelectedRoute = () => {
    console.log('Clearing all routes');

    // Revert any highlighted marker
    highlightMatchMarker(null);

    // Clear the selected match route
    if (selectedMatchRoute) {
      selectedMatchRoute.setMap(null);
      setSelectedMatchRoute(null);
    }
    
    // Clear ALL polylines
    polylinesRef.current.forEach(polyline => {
      if (polyline) polyline.setMap(null);
    });
    polylinesRef.current = [];
    
    setSelectedMatch(null);
  };
  
  // Direct function to fetch and display a match's route (no toggling)
  const fetchAndDisplayMatchRoute = (match) => {
    if (!match) return;
    
    // Clear any existing selected match route
    if (selectedMatchRoute) {
      selectedMatchRoute.setMap(null);
    }
    
    // Get match route polyline from database
    fetchMatchRoutePolyline(match)
      .then(polyline => {
        if (!polyline) {
          console.warn("No polyline found for match");
          addToast({
            type: 'warning',
            title: t('posts.no_route_data'),
            description: t('posts.no_route_data_message')
          });
          return;
        }
        
        // Decode the polyline and draw it
        const path = decodePolyline(polyline);
        if (path.length < 2) {
          console.warn("Not enough points in match polyline");
          return;
        }
        
        // Determine color based on role
        let strokeColor;
        if (match.match_role === 'Driver') {
          strokeColor = "#4285F4"; // Blue
        } else if (match.match_role === 'Passenger') {
          strokeColor = "#9C27B0"; // Purple  
        } else if (match.match_role === 'Package') {
          strokeColor = "#FBBC05"; // Yellow
        } else {
          strokeColor = "#FF5722"; // Orange
        }
        
        // Create the polyline
        const matchPolyline = new window.google.maps.Polyline({
          path,
          geodesic: true,
          strokeColor,
          strokeOpacity: 0.7,
          strokeWeight: 4,
          zIndex: 400
        });
        
        matchPolyline.setMap(map);
        setSelectedMatchRoute(matchPolyline);
        
        // Store the polyline in the reference array for later cleanup
        polylinesRef.current.push(matchPolyline);
        
        // Update bounds to include this route
        const newBounds = new window.google.maps.LatLngBounds();
        
        // Include main route points
        if (mainPolylineRef.current) {
          mainPolylineRef.current.getPath().forEach(point => {
            newBounds.extend(point);
          });
        }
        
        // Include match route points
        path.forEach(point => {
          newBounds.extend(point);
        });
        
        // Update the map bounds
        if (!newBounds.isEmpty()) {
          map.fitBounds(newBounds);
        }
      })
      .catch(error => {
        console.error("Error fetching match route:", error);
        addToast({
          type: 'error',
          title: t('errors.error'),
          description: t('errors.route_fetch_failed')
        });
      });
  };
  
  // Select a match: clear prior routes, draw this one, notify parent, close popover.
  const selectMatch = (match) => {
    if (!match) return;
    polylinesRef.current.forEach(p => { if (p) p.setMap(null); });
    polylinesRef.current = [];
    if (selectedMatchRoute) {
      selectedMatchRoute.setMap(null);
      setSelectedMatchRoute(null);
    }
    setClusterPopover(null);
    setSelectedMatch(match);
    highlightMatchMarker(match);
    fetchAndDisplayMatchRoute(match);
    if (onViewMatch) onViewMatch(match);
  };

  // Cluster click → open a list popover of the posts stacked at that location.
  const handleClusterClick = (event, cluster) => {
    const clusterMatches = (cluster?.markers || [])
      .map(m => m.matchData)
      .filter(Boolean);
    if (clusterMatches.length === 0) return;
    if (clusterMatches.length === 1) {
      selectMatch(clusterMatches[0]);
      return;
    }
    let pos = null;
    const dom = event?.domEvent;
    const rect = mapRef.current?.getBoundingClientRect();
    if (dom && rect && typeof dom.clientX === 'number') {
      pos = {
        x: Math.min(Math.max(dom.clientX - rect.left, 8), Math.max(rect.width - 8, 8)),
        y: Math.min(Math.max(dom.clientY - rect.top, 8), Math.max(rect.height - 8, 8))
      };
    }
    setShowFilters(false);
    setPopoverPos(null); // hide until measured + clamped (see the layout effect)
    setClusterPopover({ matches: clusterMatches, pos });
  };

  // The clusterer is created once, so keep its onClusterClick pointing at the
  // latest closure (which captures current selectedMatchRoute etc.).
  useEffect(() => {
    clusterClickRef.current = handleClusterClick;
  });

  // Close the filter dropdown on outside-click / Escape.
  useEffect(() => {
    if (!showFilters) return undefined;
    const onDocMouseDown = (e) => {
      if (!e.target.closest('.route-map-actions')) setShowFilters(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowFilters(false); };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showFilters]);

  // Close the cluster popover on Escape.
  useEffect(() => {
    if (!clusterPopover) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setClusterPopover(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [clusterPopover]);

  // Keep the whole popover inside the map bounds. Measure it after render but
  // before paint (useLayoutEffect), so centering on the click point can't slide
  // it off the edges. The first pass renders hidden at the raw point, this pass
  // sets the clamped position and reveals it — all before the browser paints.
  useLayoutEffect(() => {
    const el = clusterPopoverRef.current;
    const body = mapBodyRef.current;
    if (!el || !body || !clusterPopover?.pos) return;
    const margin = 8;
    const bw = body.clientWidth;
    const bh = body.clientHeight;
    const pw = el.offsetWidth;
    const ph = el.offsetHeight;

    let left = clusterPopover.pos.x - pw / 2;
    left = Math.max(margin, Math.min(left, bw - pw - margin));

    let top = clusterPopover.pos.y + 12;
    if (top + ph + margin > bh) top = clusterPopover.pos.y - ph - 12; // flip above if no room below
    top = Math.max(margin, Math.min(top, bh - ph - margin));

    setPopoverPos({ left, top });
  }, [clusterPopover]);

  // Fetch a match's route polyline from the database
  const fetchMatchRoutePolyline = async (match) => {
    try {
      if (!match || !match.match_id || !match.match_type) {
        throw new Error("Invalid match data");
      }
      
      console.log('Fetching route polyline for match:', match);
      
      // Determine the table based on match type
      let table, idColumn;
      if (match.match_type === 'post') {
        table = 'posts';
        idColumn = 'id';
      } else if (match.match_type === 'package') {
        table = 'packages';
        idColumn = 'id';
      } else {
        throw new Error("Unsupported match type: " + match.match_type);
      }
      
      // Supabase query to get the polyline
      const { data, error } = await supabase
        .from(table)
        .select('route_polyline')
        .eq(idColumn, match.match_id)
        .single();
      
      if (error) {
        throw error;
      }
      
      const polyline = data?.route_polyline || null;
      console.log('Fetched polyline:', polyline ? 'Found' : 'Not found');
      
      // For all post types (driver, passenger, package), use the stored polyline if available
      if (polyline) {
        return polyline;
      }
      
      // If no polyline is found in the database, show a user-friendly error
      console.error('No route polyline found for match ID:', match.match_id);
      addToast({
        type: 'warning',
        title: t('posts.no_route_data'),
        description: t('posts.match_no_route_message') || 'This match does not have a stored route. Please check the post details.'
      });
      
      // Important: Return null instead of creating a fallback line
      // This prevents drawing an incorrect route
      return null;
    } catch (error) {
      console.error("Error fetching match route polyline:", error);
      throw error;
    }
  };

  // Toggle expanded (taller) map. We expand the map inline within the modal
  // rather than breaking out to a fixed-position fullscreen, which never worked
  // here — the map lives inside a portaled, transformed modal, so `position:
  // fixed` was trapped and layered behind the modal.
  const toggleFullView = () => {
    const next = !showFullMapView;
    setShowFullMapView(next);
    setClusterPopover(null);
    // Grow/shrink the whole modal to fullscreen (handled by the parent).
    if (onExpandChange) onExpandChange(next);

    // Let the modal + height change apply, then let Google Maps re-measure + re-fit.
    setTimeout(() => {
      if (!map) return;
      window.google.maps.event.trigger(map, 'resize');
      fitToContent();
    }, 320);
  };

  // Recenter map function
  const recenterMap = () => {
    try {
      fitToContent();
    } catch (e) {
      console.error("Error re-centering map:", e);
    }
  };

  // Handle radius change with subscription limit checking
  const handleRadiusChange = (e) => {
    const newRadius = parseInt(e.target.value, 10);
    
    // Check if new radius exceeds subscription limit
    if (newRadius > maxAllowedRadius) {
      addToast({
        type: 'warning',
        title: t('errors.limit_reached') || 'Subscription Limit',
        description: `Your current plan allows maximum ${maxAllowedRadius}km matching radius. Upgrade for wider search range!`
      });
      
      // Ask if they want to view subscription plans
      if (window.confirm(t('chat.view_subscription_plans') || 'Would you like to view our subscription plans?')) {
        navigate('/subscription-plans');
        return;
      }
      
      // Set to max allowed if user declines to view plans
      setSearchRadius(maxAllowedRadius);
      return;
    }
    
    // Proceed with the update if within limits
    setSearchRadius(newRadius);
    
    // Update post in parent component if possible
    if (post && post.id) {
      const updatePostRadius = async () => {
        try {
          await supabase
            .from(post.isPackage ? 'packages' : 'posts')
            .update({ search_radius_km: newRadius })
            .eq('id', post.id);
          
          console.log(`Updated search radius to ${newRadius}km for ${post.isPackage ? 'package' : 'post'} ID: ${post.id}`);
          
          // Refresh matches if needed
          if (onFilterChange) {
            onFilterChange({ searchRadius: newRadius });
            
            // Dispatch an event to trigger a refresh
            const refreshEvent = new CustomEvent('refreshMatches', {
              detail: { radius: newRadius }
            });
            window.dispatchEvent(refreshEvent);
            
            // Display a toast to indicate changes are being applied
            addToast({
              type: 'info',
              title: t('matches.radius_updated') || 'Search Radius Updated',
              description: t('matches.refreshing_matches') || 'Refreshing matches with new radius...'
            });
          }
        } catch (error) {
          console.error('Error updating search radius:', error);
          addToast({
            type: 'error',
            title: t('errors.update_failed') || 'Update Failed',
            description: t('errors.radius_update_failed') || 'Failed to update search radius.'
          });
        }
      };
      
      updatePostRadius();
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (filterType) => {
    const newFilters = {
      ...filters,
      [filterType]: !filters[filterType]
    };
    
    setFilters(newFilters);
    
    // Notify parent component if needed
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  // Display loading state
  if (isMapLoading) {
    return (
      <MapLoadingSkeleton
        height={500}
        text={t('app.loading_map')}
      />
    );
  }

  // Display error state
  if (error) {
    return (
      <div className="route-map-error">
        <FaExclamationTriangle />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className={`route-map-view ${showFullMapView ? 'expanded' : ''}`}>
      <div className="route-map-header">
        <div className="route-map-actions">
          {showRadiusSelector && (
            <div className="radius-control">
              <select value={searchRadius} onChange={handleRadiusChange} title={t('matches.search_radius') || 'Search Radius'}>
                <option value="5">5 km</option>
                <option value="10" disabled={maxAllowedRadius < 10}>10 km</option>
                <option value="15" disabled={maxAllowedRadius < 15}>15 km</option>
                <option value="20" disabled={maxAllowedRadius < 20}>20 km</option>
                <option value="30" disabled={maxAllowedRadius < 30}>30 km</option>
              </select>
            </div>
          )}
          <button 
            className="recenter-button map-action-button" 
            onClick={recenterMap}
            title={t('posts.recenter_map')}
          >
            <FaLocationArrow />
          </button>
          <button
            className={`expand-button map-action-button ${showFullMapView ? 'is-active' : ''}`}
            onClick={toggleFullView}
            title={showFullMapView ? t('posts.minimize_map') : t('posts.maximize_map')}
          >
            {showFullMapView ? <FaCompressAlt /> : <FaExpandAlt />}
          </button>
          <button
            className={`filter-button map-action-button ${showFilters ? 'is-active' : ''}`}
            onClick={() => { setShowFilters(s => !s); setClusterPopover(null); }}
            title={t('posts.filter_matches')}
            aria-expanded={showFilters}
          >
            <FaFilter />
          </button>
          <div className={`filter-dropdown ${showFilters ? 'show' : ''}`}>
            <div className="filter-header">
              <FaSlidersH /> {t('posts.filter_matches')}
            </div>
            <div className="filter-item">
              <label>
                <input 
                  type="checkbox" 
                  checked={filters.showDrivers} 
                  onChange={() => handleFilterChange('showDrivers')} 
                />
                <div className="filter-color driver"></div>
                {t('posts.show_drivers')}
              </label>
            </div>
            <div className="filter-item">
              <label>
                <input 
                  type="checkbox" 
                  checked={filters.showPassengers} 
                  onChange={() => handleFilterChange('showPassengers')} 
                />
                <div className="filter-color passenger"></div>
                {t('posts.show_passengers')}
              </label>
            </div>
            <div className="filter-item">
              <label>
                <input 
                  type="checkbox" 
                  checked={filters.showPackages} 
                  onChange={() => handleFilterChange('showPackages')} 
                />
                <div className="filter-color package"></div>
                {t('posts.show_packages')}
              </label>
            </div>
          </div>
        </div>
      </div>
      
      <div className="route-map-body" ref={mapBodyRef}>
        <div
          className="route-map-container"
          ref={mapRef}
          style={{ height: showFullMapView ? 'max(320px, calc(95vh - 190px))' : '400px' }}
        ></div>

        {(!matches || matches.length === 0) && (
          <div className="route-map-empty-hint">
            {t('matches.none_in_radius', { km: searchRadius })}
          </div>
        )}

        {clusterPopover && (
          <div
            ref={clusterPopoverRef}
            className={`cluster-popover ${clusterPopover.pos ? 'anchored' : 'centered'}`}
            style={
              clusterPopover.pos
                ? (popoverPos
                    ? { left: popoverPos.left, top: popoverPos.top }
                    : { left: clusterPopover.pos.x, top: clusterPopover.pos.y, visibility: 'hidden' })
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cluster-popover-header">
              <span>{clusterPopover.matches.length} {t('posts.matches_here') || 'matches here'}</span>
              <button
                type="button"
                className="cluster-popover-close"
                onClick={() => setClusterPopover(null)}
                aria-label={t('buttons.close') || 'Close'}
              >
                ×
              </button>
            </div>
            <div className="cluster-popover-list">
              {clusterPopover.matches.map((m, i) => (
                <button
                  type="button"
                  key={`${m.match_type}-${m.match_id}-${i}`}
                  className="cluster-popover-item"
                  onClick={() => selectMatch(m)}
                >
                  <span className="cpi-avatar">
                    {m.avatar_url ? (
                      <img src={m.avatar_url} alt={m.username || ''} />
                    ) : (
                      <FaUser />
                    )}
                  </span>
                  <span className="cpi-main">
                    <span className="cpi-name">{m.username || t('chat.unknown_user')}</span>
                    <span className="cpi-meta">
                      {m.match_role === 'Driver' && <FaCar />}
                      {m.match_role === 'Passenger' && <FaUser />}
                      {m.match_role === 'Package' && <FaBox />}
                      {m.travel_date && <span>{new Date(m.travel_date).toLocaleDateString('en-GB')}</span>}
                      {m.travel_time && <span>{String(m.travel_time).substring(0, 5)}</span>}
                    </span>
                  </span>
                  {(m.distance_km || m.distance_km === 0) && (
                    <span className="cpi-distance">{parseFloat(m.distance_km || 0).toFixed(1)} km</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostRouteMapView;